class DooProductDossier extends HTMLElement {
  connectedCallback() {
    this.gallery = this.querySelector('[data-doo-gallery]');
    this.panels = [...this.querySelectorAll('[data-gallery-panel]')];
    this.triggers = [...this.querySelectorAll('[data-gallery-trigger]')];
    this.variantSelect = this.querySelector('[data-doo-variant-select]');

    this.bindGallery();
    this.bindVariantSelector();
    this.updateColourLinks();
    this.enhanceAdminTables();
  }

  bindGallery() {
    if (!this.gallery || this.panels.length < 1) return;

    this.triggers.forEach((trigger, index) => {
      trigger.addEventListener('click', () => this.showGalleryPanel(index));
      trigger.addEventListener('keydown', (event) => this.handleGalleryKeydown(event, index));
    });

    const stage = this.querySelector('[data-doo-gallery-stage]');
    if (!stage || this.panels.length < 2) return;

    let pointerStartX = null;
    stage.addEventListener('pointerdown', (event) => {
      if (event.target.closest('button, a, video, model-viewer')) return;
      pointerStartX = event.clientX;
    });
    stage.addEventListener('pointerup', (event) => {
      if (pointerStartX === null) return;
      const distance = event.clientX - pointerStartX;
      pointerStartX = null;
      if (Math.abs(distance) < 48) return;

      const current = this.panels.findIndex((panel) => !panel.hidden);
      const direction = distance < 0 ? 1 : -1;
      const next = (current + direction + this.panels.length) % this.panels.length;
      this.showGalleryPanel(next);
    });
    stage.addEventListener('pointercancel', () => {
      pointerStartX = null;
    });
  }

  handleGalleryKeydown(event, index) {
    const handledKeys = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];
    if (!handledKeys.includes(event.key)) return;
    event.preventDefault();

    let next = index;
    if (event.key === 'ArrowLeft') next = (index - 1 + this.triggers.length) % this.triggers.length;
    if (event.key === 'ArrowRight') next = (index + 1) % this.triggers.length;
    if (event.key === 'Home') next = 0;
    if (event.key === 'End') next = this.triggers.length - 1;

    this.showGalleryPanel(next, true);
  }

  showGalleryPanel(index, focusTrigger = false) {
    if (!this.panels[index]) return;

    this.panels.forEach((panel, panelIndex) => {
      const active = panelIndex === index;
      panel.hidden = !active;
      panel.classList.toggle('is-active', active);
    });

    this.triggers.forEach((trigger, triggerIndex) => {
      const active = triggerIndex === index;
      trigger.classList.toggle('is-active', active);
      trigger.setAttribute('aria-selected', active ? 'true' : 'false');
      trigger.tabIndex = active ? 0 : -1;
    });

    if (focusTrigger && this.triggers[index]) this.triggers[index].focus();
  }

  showMediaById(mediaId) {
    if (!mediaId) return;
    const panelIndex = this.panels.findIndex((panel) => panel.dataset.mediaId === mediaId);
    if (panelIndex >= 0) this.showGalleryPanel(panelIndex);
  }

  bindVariantSelector() {
    if (!this.variantSelect) return;
    this.variantSelect.addEventListener('change', () => this.updateVariantState());
  }

  updateVariantState() {
    const option = this.variantSelect.options[this.variantSelect.selectedIndex];
    if (!option) return;

    const available = option.dataset.available === 'true';
    const price = this.querySelector('[data-doo-price-current]');
    const compare = this.querySelector('[data-doo-price-compare]');
    const button = this.querySelector('[data-doo-add-button]');
    const waitlist = this.querySelector('[data-doo-waitlist]');
    const status = this.querySelector('[data-doo-variant-status]');

    if (price && option.dataset.price) price.textContent = option.dataset.price;
    if (compare) {
      compare.textContent = option.dataset.compare || '';
      compare.hidden = !option.dataset.compare;
    }

    if (button) {
      const label = button.querySelector('[data-doo-add-label]');
      button.disabled = !available;
      if (label) label.textContent = available ? button.dataset.addLabel : button.dataset.soldLabel;
    }

    if (waitlist) waitlist.hidden = available;
    if (status) status.textContent = available ? 'Selected option is available.' : 'Selected option is sold out.';

    this.showMediaById(option.dataset.mediaId);
    this.updateVariantUrl(option.value);
    this.updateColourLinks();
  }

  updateColourLinks() {
    const option = this.variantSelect?.options[this.variantSelect.selectedIndex];
    const size = option?.dataset.size;
    this.querySelectorAll('[data-doo-colour-link]').forEach((link) => {
      const base = link.dataset.colourUrl;
      if (!base) return;
      link.setAttribute('href', base);
      if (!size) return;
      try {
        const variants = JSON.parse(link.dataset.colourSizes || '[]');
        const matches = variants.filter((variant) => variant.size === size);
        // Multiple matches could represent another option such as material: do not guess.
        if (matches.length !== 1) return;
        const url = new URL(base, window.location.origin);
        url.searchParams.set('variant', String(matches[0].id));
        link.setAttribute('href', `${url.pathname}${url.search}${url.hash}`);
      } catch {
        // Ordinary product navigation still works if size metadata is unavailable.
      }
    });
  }

  updateVariantUrl(variantId) {
    if (!variantId || window.Shopify?.designMode) return;
    const productUrl = this.dataset.productUrl;
    if (!productUrl) return;

    const url = new URL(window.location.href);
    const destination = new URL(productUrl, window.location.origin);
    if (url.pathname !== destination.pathname) return;
    url.searchParams.set('variant', variantId);
    window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
  }

  enhanceAdminTables() {
    const description = this.querySelector('[data-doo-admin-description]');
    if (!description) return;

    [...description.querySelectorAll('table')].forEach((table) => {
      if (table.parentElement?.classList.contains('doo-pdp-admin__table-scroll')) return;
      const scroll = document.createElement('div');
      scroll.className = 'doo-pdp-admin__table-scroll';
      table.parentNode.insertBefore(scroll, table);
      scroll.appendChild(table);
    });

    const apparelProfiles = ['sweatshirt', 'cap', 'tote'];
    if (!apparelProfiles.includes(this.dataset.productProfile)) return;
    const admin = this.querySelector('[data-doo-admin]');
    if (admin?.dataset.showSizeGuide !== 'true') return;
    if (this.querySelector('[data-doo-size-guide]')) return;

    const tableScroll = description.querySelector('.doo-pdp-admin__table-scroll');
    if (!tableScroll) return;

    let label = 'Size guide';
    const previous = tableScroll.previousElementSibling;
    if (previous && /^H[1-6]$/.test(previous.tagName) && /size/i.test(previous.textContent)) {
      label = previous.textContent.trim() || label;
      previous.remove();
    }

    const details = document.createElement('details');
    details.className = 'doo-pdp-admin__accordion doo-pdp-admin__generated-size-guide';
    details.dataset.dooSizeGuide = '';

    const summary = document.createElement('summary');
    const summaryLabel = document.createElement('span');
    const summaryIcon = document.createElement('span');
    summaryLabel.textContent = label;
    summaryIcon.textContent = '+';
    summaryIcon.setAttribute('aria-hidden', 'true');
    summary.append(summaryLabel, summaryIcon);

    const content = document.createElement('div');
    content.className = 'doo-pdp-admin__accordion-content rte';

    tableScroll.parentNode.insertBefore(details, tableScroll);
    content.appendChild(tableScroll);
    details.append(summary, content);
  }
}

if (!customElements.get('doo-product-dossier')) {
  customElements.define('doo-product-dossier', DooProductDossier);
}
